import { create } from 'zustand';
import {
  initAlertState,
  stepAlert,
  type AlertEvent,
  type AlertSpec,
  type AlertState,
} from '@/lib/alertStateMachine';

/**
 * Performance sampler store.
 *
 * Pure data layer — does NOT issue HTTP itself. The Performance page mounts
 * `useQueries` against the ADB toolbox and feeds the results in via
 * `pushSample`. Component unmount → React Query cancels → polling stops.
 *
 * Multi-device: state is partitioned by serial so users can switch between
 * devices without losing each device's rolling history.
 *
 * Ring buffer length is `RING_CAPACITY` (≈ 10 min at 1 Hz). Old samples are
 * dropped as new ones arrive. Recording (§12.5) copies to an independent
 * buffer that is NOT subject to the ring cap, so 30 min recordings stay
 * intact.
 */

export const RING_CAPACITY = 600;
export const RECORD_HARD_LIMIT_MS = 30 * 60_000;
export const RECORD_WARN_AT_MS = 25 * 60_000;

export interface PerfSample {
  t: number;

  // Device-wide CPU breakdown (proc.top.totals.* — already collected). The
  // legacy `cpu` field stays around as the SUM so alert specs and any
  // historical recording still read correctly.
  cpu?: number;
  cpuUser?: number;
  cpuSys?: number;
  cpuIow?: number;
  cpuIrq?: number;

  // Device-wide physical RAM (perf.deviceMem → /proc/meminfo).
  devMemUsedPct?: number;
  devMemTotalKb?: number;
  devMemAvailableKb?: number;

  // Focused-process metrics. `focusPssKb` is the total PSS; the rest are
  // the App Summary breakdown rows (Java / Native / Graphics / Code /
  // Stack / Private Other / System). `focusMemKb` legacy alias retained
  // for old recordings (mirrors focusPssKb when set).
  focusPid?: number;
  focusCpu?: number;
  focusPssKb?: number;
  focusJavaKb?: number;
  focusNativeKb?: number;
  focusGraphicsKb?: number;
  focusCodeKb?: number;
  focusStackKb?: number;
  focusOtherKb?: number;
  focusSystemKb?: number;
  focusMemKb?: number;

  // Rendering — FPS comes from differencing `framesRendered`; jankPct +
  // p99Ms come straight from the gfxinfo summary. The frame-time
  // histogram + framestats CSV live in `useFrameStats` (separate store)
  // because they're per-frame, not per-sample.
  fps?: number;
  jankPct?: number;
  p99Ms?: number;

  // Battery + temperature. `socC` / `batteryC` come from
  // `perf.thermal.classified`; the SPA falls back to
  // `device.battery.temperature/10` for batteryC when /sys denies read.
  batteryPct?: number;
  pluggedIn?: boolean;
  socC?: number;
  batteryC?: number;
}

export type AlertId =
  | 'cpu_high'
  | 'thermal_high'
  | 'jank_high'
  | 'mem_growth_high';

export const DEFAULT_ALERTS: ReadonlyArray<AlertSpec & { id: AlertId }> = [
  { id: 'cpu_high', label: '全局 CPU > 80%', threshold: 80, direction: 'above' },
  { id: 'thermal_high', label: 'CPU 温度 > 60°C', threshold: 60, direction: 'above' },
  { id: 'jank_high', label: 'Jank > 5%', threshold: 5, direction: 'above' },
];

export interface Recording {
  startedAt: number;
  samples: PerfSample[];
  warned: boolean;
}

export interface DevicePerfState {
  samples: PerfSample[];
  intervalMs: 1000 | 2000 | 5000;
  focusPid?: number;
  focusPackage?: string;
  /** When true the page polls `app.foreground` and re-focuses the perf
   *  charts whenever the foreground package changes. Default ON: a fresh
   *  visit to the Performance tab should immediately show Memory/FPS
   *  curves for whatever the user is currently looking at on the device,
   *  without forcing them to scroll the process table first. */
  followForeground: boolean;
  recording: Recording | null;
  alerts: Record<AlertId, AlertState>;
  /** Sliding window of pending toast events to surface (drained by the page). */
  pendingEvents: AlertEvent[];
  /** Notice flag from the most-recent proc.top — used for the empty-state row. */
  procTopNotice?: 'unsupported_top_format' | 'empty_output';
}

export interface PerfState {
  byserial: Record<string, DevicePerfState>;
  ensureSerial: (serial: string) => void;
  pushSample: (serial: string, partial: PerfSample) => void;
  setIntervalMs: (serial: string, intervalMs: DevicePerfState['intervalMs']) => void;
  setFocus: (serial: string, focusPid?: number, focusPackage?: string) => void;
  /** Toggle the "follow foreground app" auto-focus mode. */
  setFollowForeground: (serial: string, follow: boolean) => void;
  setProcTopNotice: (serial: string, notice: DevicePerfState['procTopNotice']) => void;
  startRecord: (serial: string, now: number) => void;
  stopRecord: (serial: string) => Recording | null;
  drainEvents: (serial: string) => AlertEvent[];
  /** Hard-reset (used on device switch / clear). */
  reset: (serial: string) => void;
}

function freshDeviceState(): DevicePerfState {
  const now = Date.now();
  const alerts: Record<AlertId, AlertState> = {
    cpu_high: initAlertState(now),
    thermal_high: initAlertState(now),
    jank_high: initAlertState(now),
    mem_growth_high: initAlertState(now),
  };
  return {
    samples: [],
    intervalMs: 1000,
    followForeground: true,
    recording: null,
    alerts,
    pendingEvents: [],
  };
}

export const usePerfSampler = create<PerfState>((set, get) => ({
  byserial: {},

  ensureSerial: (serial) => {
    if (get().byserial[serial]) return;
    set((s) => ({ byserial: { ...s.byserial, [serial]: freshDeviceState() } }));
  },

  pushSample: (serial, partial) => {
    set((s) => {
      const cur = s.byserial[serial] ?? freshDeviceState();
      // Merge into the latest sample if the same timestamp is pushed twice
      // (Performance page fires multiple useQueries at the same tick). This
      // keeps the ring slot count honest and the chart smooth.
      const samples = cur.samples.slice();
      const last = samples[samples.length - 1];
      const next: PerfSample =
        last && Math.abs(last.t - partial.t) < 200
          ? { ...last, ...partial }
          : partial;
      if (last && next === partial) {
        samples.push(next);
      } else if (last) {
        samples[samples.length - 1] = next;
      } else {
        samples.push(next);
      }
      // Trim ring buffer.
      if (samples.length > RING_CAPACITY) {
        samples.splice(0, samples.length - RING_CAPACITY);
      }
      // Update recording buffer (independent, no ring cap). Auto-stop at
      // hard limit; emit no extra event, the page hook reads RECORD state
      // each render and stops the recording itself when needed.
      let recording = cur.recording;
      if (recording) {
        recording = { ...recording, samples: [...recording.samples, next] };
        if (
          !recording.warned &&
          next.t - recording.startedAt >= RECORD_WARN_AT_MS
        ) {
          recording = { ...recording, warned: true };
        }
      }
      // Run alert state machines.
      const newAlerts: Record<AlertId, AlertState> = { ...cur.alerts };
      const newEvents: AlertEvent[] = [];
      const stepOne = (id: AlertId, value: number | undefined): void => {
        const spec = DEFAULT_ALERTS.find((a) => a.id === id);
        if (!spec) return;
        const { state, event } = stepAlert(spec, newAlerts[id], value, next.t);
        newAlerts[id] = state;
        if (event) newEvents.push(event);
      };
      stepOne('cpu_high', next.cpu);
      stepOne('thermal_high', next.socC);
      stepOne('jank_high', next.jankPct);

      return {
        byserial: {
          ...s.byserial,
          [serial]: {
            ...cur,
            samples,
            recording,
            alerts: newAlerts,
            pendingEvents: newEvents.length
              ? [...cur.pendingEvents, ...newEvents]
              : cur.pendingEvents,
          },
        },
      };
    });
  },

  setIntervalMs: (serial, intervalMs) => {
    set((s) => {
      const cur = s.byserial[serial] ?? freshDeviceState();
      return {
        byserial: { ...s.byserial, [serial]: { ...cur, intervalMs } },
      };
    });
  },

  setFocus: (serial, focusPid, focusPackage) => {
    set((s) => {
      const cur = s.byserial[serial] ?? freshDeviceState();
      return {
        byserial: {
          ...s.byserial,
          [serial]: { ...cur, focusPid, focusPackage },
        },
      };
    });
  },

  setFollowForeground: (serial, follow) => {
    set((s) => {
      const cur = s.byserial[serial] ?? freshDeviceState();
      return {
        byserial: {
          ...s.byserial,
          [serial]: { ...cur, followForeground: follow },
        },
      };
    });
  },

  setProcTopNotice: (serial, notice) => {
    set((s) => {
      const cur = s.byserial[serial] ?? freshDeviceState();
      return {
        byserial: {
          ...s.byserial,
          [serial]: { ...cur, procTopNotice: notice },
        },
      };
    });
  },

  startRecord: (serial, now) => {
    set((s) => {
      const cur = s.byserial[serial] ?? freshDeviceState();
      if (cur.recording) return s; // idempotent
      return {
        byserial: {
          ...s.byserial,
          [serial]: {
            ...cur,
            recording: { startedAt: now, samples: [], warned: false },
          },
        },
      };
    });
  },

  stopRecord: (serial) => {
    const cur = get().byserial[serial];
    const rec = cur?.recording ?? null;
    if (cur) {
      set((s) => ({
        byserial: {
          ...s.byserial,
          [serial]: { ...cur, recording: null },
        },
      }));
    }
    return rec;
  },

  drainEvents: (serial) => {
    const cur = get().byserial[serial];
    if (!cur || cur.pendingEvents.length === 0) return [];
    const events = cur.pendingEvents;
    set((s) => ({
      byserial: {
        ...s.byserial,
        [serial]: { ...cur, pendingEvents: [] },
      },
    }));
    return events;
  },

  reset: (serial) => {
    set((s) => ({ byserial: { ...s.byserial, [serial]: freshDeviceState() } }));
  },
}));

/* ---------- Export helpers (§12.5) ---------- */

const CSV_FIELDS: Array<keyof PerfSample> = [
  't',
  'cpu',
  'cpuUser',
  'cpuSys',
  'cpuIow',
  'cpuIrq',
  'devMemUsedPct',
  'devMemTotalKb',
  'devMemAvailableKb',
  'fps',
  'jankPct',
  'p99Ms',
  'batteryPct',
  'pluggedIn',
  'socC',
  'batteryC',
  'focusPid',
  'focusCpu',
  'focusPssKb',
  'focusJavaKb',
  'focusNativeKb',
  'focusGraphicsKb',
  'focusCodeKb',
  'focusStackKb',
  'focusOtherKb',
  'focusSystemKb',
];

/** UTF-8 BOM — makes Excel open Chinese process names without mojibake. */
export const UTF8_BOM = '\uFEFF';

export function samplesToCsv(samples: PerfSample[]): string {
  const head = CSV_FIELDS.join(',');
  const lines = samples.map((s) =>
    CSV_FIELDS.map((k) => {
      const v = s[k];
      return v === undefined ? '' : String(v);
    }).join(',')
  );
  return UTF8_BOM + head + '\n' + lines.join('\n') + (lines.length ? '\n' : '');
}

export interface ExportEnvelope {
  meta: {
    serial: string;
    focusPackage?: string;
    intervalMs: number;
    startedAt: number;
    endedAt: number;
    version: 1;
  };
  samples: PerfSample[];
}

export function samplesToJson(envelope: ExportEnvelope): string {
  return JSON.stringify(envelope, null, 2);
}

export function downloadAs(filename: string, content: string, mime: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/** Build the standard filename `adh-perf-<serial>-<ISO>.{ext}`. */
export function exportFilename(serial: string, ext: 'csv' | 'json'): string {
  // Replace illegal Windows filename chars in the ISO timestamp.
  const iso = new Date().toISOString().replace(/[:.]/g, '-');
  const safeSerial = serial.replace(/[^A-Za-z0-9_.-]/g, '_');
  return `adh-perf-${safeSerial}-${iso}.${ext}`;
}
